import { ApolloClient, NormalizedCacheObject } from '@apollo/client';

import getResponseTerms from '../getResponseTerms';

import getMetrics from './getMetrics';
import { filterMetricsForTotals } from './filterMetricsForTotals';

const getAllCurrentAndPreviousTermResponses = async ({
  chartDef,
  usedMetrics,
  filterInput,
  isDashboardGadget,
  currentDateScope,
  previousDateScope,
  toMetricInput,
  client,
  groupByField,
  comparison,
}: {
  chartDef: V5ChartDefinition;
  usedMetrics: Metrics.Metric[];
  filterInput: FilterInput;
  isDashboardGadget?: boolean;
  currentDateScope: DateRangeInput;
  comparison?: PersistedComparisonType;
  previousDateScope: DateRangeInput;
  toMetricInput: (metric: Metrics.NormalMetric | MetricInput) => MetricInput;
  client: ApolloClient<NormalizedCacheObject>;
  groupByField: string;
}) => {
  const currentPeriodMetrics = await getMetrics({
    chartDef,
    filterInput,
    usedMetrics,
    isDashboardGadget,
    dateScope: currentDateScope,
    toMetricInput,
    client,
    isIgnoringLimit: true,
  });
  const currentTerms = getResponseTerms(
    currentPeriodMetrics,
    groupByField,
    chartDef.dimensionA,
  );
  const previousPeriodMetrics = await getMetrics({
    chartDef,
    filterInput,
    usedMetrics,
    isDashboardGadget,
    dateScope: previousDateScope,
    toMetricInput,
    client,
    isIgnoringLimit: true,
  });
  const previousTerms = getResponseTerms(
    previousPeriodMetrics,
    groupByField,
    chartDef.dimensionA,
  );
  if (!!currentTerms && !!previousTerms) {
    const currentPeriodMissingTermMetrics = await (async () => {
      const termsToFindInCurrent = previousTerms.filter(
        (t) => !currentTerms.includes(t),
      );

      if (termsToFindInCurrent.length === 0) {
        return undefined;
      }

      const keywordFilter = {
        field: groupByField,
        values: termsToFindInCurrent,
      } as KeywordFilter;

      return getMetrics({
        chartDef,
        filterInput,
        usedMetrics,
        isDashboardGadget,
        dateScope: currentDateScope,
        toMetricInput,
        client,
        limitedKeywordFilter: keywordFilter,
        isIgnoringLimit: true,
      });
    })();
    if (currentPeriodMissingTermMetrics) {
      currentPeriodMetrics.push(...currentPeriodMissingTermMetrics);
    }
    const previousPeriodMissingTermMetrics = await (async () => {
      const termsToFindInPrevious = currentTerms.filter(
        (t) => !previousTerms.includes(t),
      );

      if (termsToFindInPrevious.length === 0) {
        return undefined;
      }

      const keywordFilter = {
        field: groupByField,
        values: termsToFindInPrevious,
      } as KeywordFilter;

      return getMetrics({
        chartDef,
        filterInput,
        usedMetrics,
        isDashboardGadget,
        dateScope: previousDateScope,
        toMetricInput,
        client,
        limitedKeywordFilter: keywordFilter,
        isIgnoringLimit: true,
      });
    })();
    if (previousPeriodMissingTermMetrics) {
      previousPeriodMetrics.push(...previousPeriodMissingTermMetrics);
    }
  }

  const totalsMetrics = filterMetricsForTotals({ usedMetrics, chartDef });
  const totalsRequest =
    totalsMetrics.length > 0
      ? getMetrics({
          chartDef,
          filterInput,
          getTotals: true,
          usedMetrics: totalsMetrics,
          isDashboardGadget,
          dateScope: currentDateScope,
          toMetricInput,
          client,
          isIgnoringLimit: true,
        })
      : Promise.resolve([]);
  const totalsComparisonRequest =
    comparison &&
    chartDef.series.some((s) => !!s.matrixCellType) &&
    totalsMetrics.length > 0
      ? getMetrics({
          chartDef,
          filterInput,
          getTotals: true,
          usedMetrics: totalsMetrics,
          isDashboardGadget,
          dateScope: previousDateScope,
          toMetricInput,
          client,
          isIgnoringLimit: true,
        })
      : Promise.resolve([]);
  const [totalsResponse, totalsComparisonResponse] = await Promise.all([
    totalsRequest,
    totalsComparisonRequest,
  ]);

  const responses = [
    currentPeriodMetrics,
    totalsResponse,
    previousPeriodMetrics,
    totalsComparisonResponse,
  ];

  return responses;
};

export default getAllCurrentAndPreviousTermResponses;
